import type { ClassificationCodesType, ClassificationObjectClassType } from "@/dataProviders/ClassificationCodes";

/**
 * The two rules `BimCodeSelect` needs to publish the classification it is *showing*.
 *
 * Both copies of that control (the create dialog's and the material dialog's) used to treat the
 * object class as private UI state: it reached the parent only as a side-car of a code selection,
 * carrying whatever value the control happened to hold at that moment. For CCI, CCS and Uniclass a
 * code resolves only when paired with its object class, so the halves going missing is what put a
 * classified material under "No classification code" in the dashboard's Project Breakdown
 * (RTLCA-5774).
 */

/**
 * The object class a system with only one of them needs no choice about — Uniclass has just "EF".
 * The control shows it as selected, so it has to be part of what a save carries: a material holding
 * the code alone reads as fully classified in the dialog while the dashboard has nothing to resolve
 * the code with.
 *
 * Undefined for CCI and CCS (two object classes each, a real choice) and for the systems that have
 * none at all.
 */
export function soleObjectClassCode(objectClasses: ClassificationObjectClassType | null | undefined): string | undefined {
  if (!Array.isArray(objectClasses) || objectClasses.length !== 1) return undefined;
  return objectClasses[0]?.code || undefined;
}

/**
 * Whether `code` is still a selection once `objectClass` is the chosen one.
 *
 * For CCI and CCS the same code exists once per object class and the two rows mean entirely
 * different things (CCS "BA" is both "[T] Groundworks structure" and "[L] Electrical potential
 * converting component"), so switching object class can invalidate the code that was picked. The
 * code select's options are filtered by object class and clear themselves when it changes, so a
 * code that no longer belongs is no longer on screen either — and publishing it against the new
 * object class would resolve to nothing.
 *
 * A code with no object class to check it against (a system without them) always belongs.
 */
export function codeBelongsToObjectClass(
  codes: ClassificationCodesType,
  code: string | undefined,
  objectClass: string | undefined
): boolean {
  if (!code) return false;
  if (!objectClass) return true;
  return codes.some((entry) => entry.code === code && entry.objectClass?.code === objectClass);
}
